import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner } from '../../../shared/components';
import type { EmailBrandPreview } from '../services/emailBrandingService';
import {
  EmailLayoutAlign,
  EmailLayoutBlock,
  EmailLayoutBlockType,
  EmailLayoutDetail,
  EmailLayoutListResponse,
  createEmailLayout,
  deleteEmailLayout,
  duplicateEmailLayout,
  getEmailLayout,
  listEmailLayouts,
  previewEmailLayout,
  setDefaultEmailLayout,
  testSendEmailLayout,
  updateEmailLayout,
} from '../services/emailLayoutService';
import { ChipGroup, Field, StatusPill } from './emailFormParts';
import styles from './EmailConnectorTab.module.css';
import brandStyles from './EmailBrandingPanel.module.css';
import layoutStyles from './EmailLayoutsEditor.module.css';

interface Draft {
  name: string;
  blocks: EmailLayoutBlock[];
}

const PREVIEW_DEBOUNCE_MS = 450;
const ADDABLE_TYPES: EmailLayoutBlockType[] = ['text', 'button', 'image', 'divider', 'spacer', 'header', 'footer'];
const FIXED_TYPES: EmailLayoutBlockType[] = ['header', 'content', 'footer'];

const draftFromDetail = (detail: EmailLayoutDetail): Draft => ({
  name: detail.name,
  blocks: detail.document.blocks.map(block => ({ ...block })),
});

const newBlock = (type: EmailLayoutBlockType): EmailLayoutBlock => {
  switch (type) {
    case 'text':
      return { type, text: '', align: 'left' };
    case 'button':
      return { type, label: '', url: 'https://', align: 'left' };
    case 'image':
      return { type, imageUrl: 'https://', altText: '', width: 520, align: 'center' };
    case 'spacer':
      return { type, height: 16 };
    default:
      return { type };
  }
};

/**
 * Header stays first and footer stays last, matching the server's rules, so a new block goes just
 * before the footer.
 */
const insertBlock = (blocks: EmailLayoutBlock[], block: EmailLayoutBlock): EmailLayoutBlock[] => {
  if (block.type === 'header') {
    return [block, ...blocks];
  }
  if (block.type === 'footer') {
    return [...blocks, block];
  }
  const footerIndex = blocks.findIndex(item => item.type === 'footer');
  return footerIndex < 0 ? [...blocks, block] : [...blocks.slice(0, footerIndex), block, ...blocks.slice(footerIndex)];
};

const canMove = (blocks: EmailLayoutBlock[], index: number, direction: -1 | 1): boolean => {
  const target = index + direction;
  if (target < 0 || target >= blocks.length) {
    return false;
  }
  const moving = blocks[index].type;
  const other = blocks[target].type;
  return moving !== 'header' && moving !== 'footer' && other !== 'header' && other !== 'footer';
};

const blockSummary = (block: EmailLayoutBlock, t: (key: string) => string): string => {
  switch (block.type) {
    case 'text':
      return block.text?.trim() || t('emailLayoutEmptyText');
    case 'button':
      return block.label?.trim() || t('emailLayoutEmptyButton');
    case 'image':
      return block.altText?.trim() || block.imageUrl || '';
    case 'spacer':
      return `${block.height ?? 16} px`;
    case 'header':
      return t('emailLayoutHeaderHelp');
    case 'content':
      return t('emailLayoutContentHelp');
    case 'footer':
      return t('emailLayoutFooterHelp');
    default:
      return '';
  }
};

interface EmailLayoutsEditorProps {
  canManage: boolean;
  /** Called after any change to the list so other views (connector layout pickers) can refresh. */
  onLayoutsChanged?: () => void;
}

export const EmailLayoutsEditor: React.FC<EmailLayoutsEditorProps> = ({ canManage, onLayoutsChanged }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();

  const [list, setList] = React.useState<EmailLayoutListResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<EmailLayoutDetail | null>(null);
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [expanded, setExpanded] = React.useState<number | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [presetId, setPresetId] = React.useState('standard');
  const [addType, setAddType] = React.useState<EmailLayoutBlockType>('text');
  const [preview, setPreview] = React.useState<EmailBrandPreview | null>(null);
  const [previewWidth, setPreviewWidth] = React.useState<'desktop' | 'mobile'>('desktop');
  const [testTo, setTestTo] = React.useState('');
  const [testResult, setTestResult] = React.useState<{ ok: boolean; message: string } | null>(null);
  const previewRequestRef = React.useRef(0);

  const refreshList = React.useCallback(async () => {
    const next = await listEmailLayouts();
    setList(next);
    return next;
  }, []);

  const select = React.useCallback(async (id: string) => {
    setBusy(true);
    try {
      const detail = await getEmailLayout(id);
      setSelected(detail);
      setDraft(draftFromDetail(detail));
      setExpanded(null);
      setTestResult(null);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('emailLayoutLoadFailed'), 'error');
    } finally {
      setBusy(false);
    }
  }, [showToast, t]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await refreshList();
        if (!cancelled && next.layouts.length > 0) {
          await select((next.layouts.find(layout => layout.isDefault) ?? next.layouts[0]).id);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : t('emailLayoutLoadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [refreshList, select, t]);

  React.useEffect(() => {
    if (!draft) {
      setPreview(null);
      return undefined;
    }
    const requestId = ++previewRequestRef.current;
    const timer = window.setTimeout(() => {
      previewEmailLayout({ blocks: draft.blocks })
        .then(result => {
          if (previewRequestRef.current === requestId) {
            setPreview(result);
          }
        })
        .catch(() => {
          if (previewRequestRef.current === requestId) {
            setPreview(null);
          }
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft]);

  const baseline = React.useMemo(() => (selected ? draftFromDetail(selected) : null), [selected]);
  const isDirty = Boolean(draft && baseline && JSON.stringify(draft) !== JSON.stringify(baseline));
  const disabled = !canManage || busy;

  const run = async (action: () => Promise<void>, failureKey: string) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t(failureKey), 'error');
    } finally {
      setBusy(false);
    }
  };

  const afterChange = async (detail: EmailLayoutDetail | null) => {
    await refreshList();
    if (detail) {
      setSelected(detail);
      setDraft(draftFromDetail(detail));
    }
    onLayoutsChanged?.();
  };

  const createFromPreset = () => run(async () => {
    const preset = list?.presets.find(item => item.id === presetId);
    const taken = new Set(list?.layouts.map(layout => layout.name));
    const stem = preset?.name ?? t('emailLayoutNewName');
    let name = stem;
    for (let copy = 2; taken.has(name); copy += 1) {
      name = `${stem} ${copy}`;
    }
    const detail = await createEmailLayout({ name, presetId });
    await afterChange(detail);
    setExpanded(null);
    showToast(t('emailLayoutCreated'), 'success');
  }, 'emailLayoutSaveFailed');

  const save = () => run(async () => {
    if (!selected || !draft) {
      return;
    }
    const detail = await updateEmailLayout(selected.id, draft.name, { blocks: draft.blocks });
    await afterChange(detail);
    showToast(t('emailLayoutSaved'), 'success');
  }, 'emailLayoutSaveFailed');

  const duplicate = () => run(async () => {
    if (!selected) {
      return;
    }
    await afterChange(await duplicateEmailLayout(selected.id));
  }, 'emailLayoutSaveFailed');

  const makeDefault = () => run(async () => {
    if (!selected) {
      return;
    }
    await afterChange(await setDefaultEmailLayout(selected.id));
  }, 'emailLayoutSaveFailed');

  const remove = () => run(async () => {
    if (!selected || !window.confirm(t('emailLayoutDeleteConfirm', { name: selected.name }))) {
      return;
    }
    await deleteEmailLayout(selected.id);
    setSelected(null);
    setDraft(null);
    const next = await refreshList();
    onLayoutsChanged?.();
    if (next.layouts.length > 0) {
      await select((next.layouts.find(layout => layout.isDefault) ?? next.layouts[0]).id);
    }
  }, 'emailLayoutSaveFailed');

  const sendTest = () => run(async () => {
    if (!selected) {
      return;
    }
    const result = await testSendEmailLayout(selected.id, testTo.trim());
    setTestResult({ ok: result.succeeded, message: result.message ?? '' });
  }, 'emailLayoutTestFailed');

  const updateBlock = (index: number, patch: Partial<EmailLayoutBlock>) =>
    setDraft(prev => (prev ? { ...prev, blocks: prev.blocks.map((block, i) => (i === index ? { ...block, ...patch } : block)) } : prev));

  const moveBlock = (index: number, direction: -1 | 1) =>
    setDraft(prev => {
      if (!prev || !canMove(prev.blocks, index, direction)) {
        return prev;
      }
      const blocks = [...prev.blocks];
      [blocks[index], blocks[index + direction]] = [blocks[index + direction], blocks[index]];
      return { ...prev, blocks };
    });

  const removeBlock = (index: number) => {
    setDraft(prev => (prev ? { ...prev, blocks: prev.blocks.filter((_, i) => i !== index) } : prev));
    setExpanded(null);
  };

  const availableAddTypes = ADDABLE_TYPES.filter(type =>
    (type !== 'header' || !draft?.blocks.some(block => block.type === 'header'))
    && (type !== 'footer' || !draft?.blocks.some(block => block.type === 'footer')));
  const effectiveAddType = availableAddTypes.includes(addType) ? addType : availableAddTypes[0];

  const addBlock = () => {
    if (!draft || !effectiveAddType) {
      return;
    }
    const block = newBlock(effectiveAddType);
    const blocks = insertBlock(draft.blocks, block);
    setDraft({ ...draft, blocks });
    setExpanded(blocks.indexOf(block));
  };

  const alignChips = (index: number, block: EmailLayoutBlock) => (
    <ChipGroup<EmailLayoutAlign>
      value={block.align ?? 'left'}
      onChange={value => updateBlock(index, { align: value })}
      disabled={disabled}
      ariaLabel={t('emailBrandAlignment')}
      options={[
        { id: 'left', label: t('emailBrandAlignLeft'), icon: 'alignLeft' },
        { id: 'center', label: t('emailBrandAlignCenter'), icon: 'alignCenter' },
        { id: 'right', label: t('emailLayoutAlignRight'), icon: 'alignRight' },
      ]}
    />
  );

  const textInput = (index: number, key: keyof EmailLayoutBlock, label: string, placeholder?: string, wide = false) => (
    <Field label={label} wide={wide}>
      <input
        className="text-input"
        value={String(draft?.blocks[index]?.[key] ?? '')}
        onChange={event => updateBlock(index, { [key]: event.target.value })}
        placeholder={placeholder}
        disabled={disabled}
      />
    </Field>
  );

  const renderBlockFields = (block: EmailLayoutBlock, index: number) => {
    switch (block.type) {
      case 'text':
        return (
          <div className="company-form-grid">
            <Field label={t('emailLayoutText')} wide>
              <textarea className="text-input" rows={3} value={block.text ?? ''} onChange={event => updateBlock(index, { text: event.target.value })} disabled={disabled} />
            </Field>
            {textInput(index, 'color', t('emailBrandTextColor'), t('emailLayoutColorDefault'))}
            <div className={styles.field}><span>{t('emailBrandAlignment')}</span>{alignChips(index, block)}</div>
          </div>
        );
      case 'button':
        return (
          <div className="company-form-grid">
            {textInput(index, 'label', t('emailLayoutButtonLabel'))}
            {textInput(index, 'url', t('emailLayoutButtonUrl'), 'https://')}
            {textInput(index, 'backgroundColor', t('emailLayoutButtonColor'), t('emailLayoutColorDefault'))}
            {textInput(index, 'color', t('emailLayoutButtonTextColor'), t('emailLayoutColorAuto'))}
            <div className={styles.field}><span>{t('emailBrandAlignment')}</span>{alignChips(index, block)}</div>
          </div>
        );
      case 'image':
        return (
          <div className="company-form-grid">
            {textInput(index, 'imageUrl', t('emailLayoutImageUrl'), 'https://', true)}
            {textInput(index, 'url', t('emailLayoutImageLink'), 'https://')}
            {textInput(index, 'altText', t('emailBrandLogoAlt'))}
            <Field label={t('emailLayoutImageWidth', { width: block.width ?? 520 })}>
              <input type="range" min={40} max={520} step={4} value={block.width ?? 520} onChange={event => updateBlock(index, { width: Number(event.target.value) })} disabled={disabled} />
            </Field>
            <div className={styles.field}><span>{t('emailBrandAlignment')}</span>{alignChips(index, block)}</div>
          </div>
        );
      case 'divider':
        return <div className="company-form-grid">{textInput(index, 'color', t('emailLayoutDividerColor'), '#e5e7eb')}</div>;
      case 'spacer':
        return (
          <div className="company-form-grid">
            <Field label={t('emailLayoutSpacerHeight', { height: block.height ?? 16 })}>
              <input type="range" min={4} max={96} step={4} value={block.height ?? 16} onChange={event => updateBlock(index, { height: Number(event.target.value) })} disabled={disabled} />
            </Field>
          </div>
        );
      default:
        return <p className={styles.muted}>{blockSummary(block, t)}</p>;
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (loadError) {
    return <div className="error">{loadError}</div>;
  }

  return (
    <div className={layoutStyles.root}>
      <aside className={layoutStyles.sidebar}>
        <ul className={layoutStyles.list} aria-label={t('emailLayoutsHeading')}>
          {list?.layouts.map(layout => (
            <li key={layout.id}>
              <button
                type="button"
                className={`${layoutStyles.listItem} ${selected?.id === layout.id ? layoutStyles.listItemOn : ''}`}
                onClick={() => { void select(layout.id); }}
                disabled={busy}
                aria-current={selected?.id === layout.id}
              >
                <span className={layoutStyles.listName}>{layout.name}</span>
                {layout.isDefault ? <StatusPill tone="ok">{t('emailLayoutDefault')}</StatusPill> : null}
              </button>
            </li>
          ))}
        </ul>
        {list && list.layouts.length === 0 ? <p className={styles.muted}>{t('emailLayoutNone')}</p> : null}
        {canManage ? (
          <div className={layoutStyles.create}>
            <select className="text-input" value={presetId} onChange={event => setPresetId(event.target.value)} disabled={busy} aria-label={t('emailLayoutPreset')}>
              {list?.presets.map(preset => <option key={preset.id} value={preset.id}>{t(`emailLayoutPreset_${preset.id}`, { defaultValue: preset.name })}</option>)}
            </select>
            <button type="button" className="btn btn-secondary" onClick={() => { void createFromPreset(); }} disabled={busy}>
              {t('emailLayoutCreate')}
            </button>
          </div>
        ) : null}
      </aside>

      {draft && selected ? (
        <div className={brandStyles.layout}>
          <div className={brandStyles.editor}>
            <div className="company-form-grid">
              <Field label={t('emailLayoutName')}>
                <input className="text-input" value={draft.name} onChange={event => setDraft({ ...draft, name: event.target.value })} disabled={disabled} maxLength={120} />
              </Field>
              {canManage ? (
                <div className={layoutStyles.layoutActions}>
                  {!selected.isDefault ? (
                    <button type="button" className={styles.linkButton} onClick={() => { void makeDefault(); }} disabled={busy}>{t('emailLayoutMakeDefault')}</button>
                  ) : null}
                  <button type="button" className={styles.linkButton} onClick={() => { void duplicate(); }} disabled={busy}>{t('emailLayoutDuplicate')}</button>
                  <button type="button" className={styles.linkButton} onClick={() => { void remove(); }} disabled={busy}>{t('emailLayoutDelete')}</button>
                </div>
              ) : null}
            </div>

            <h4 className={styles.subHeading}>{t('emailLayoutBlocks')}</h4>
            <ol className={layoutStyles.blocks}>
              {draft.blocks.map((block, index) => {
                const isOpen = expanded === index;
                const fixed = FIXED_TYPES.includes(block.type);
                return (
                  <li key={index} className={`${layoutStyles.block} ${isOpen ? layoutStyles.blockOpen : ''}`} data-testid={`layout-block-${index}`}>
                    <div className={layoutStyles.blockHead}>
                      <button type="button" className={layoutStyles.blockToggle} onClick={() => setExpanded(isOpen ? null : index)} aria-expanded={isOpen}>
                        <span className={layoutStyles.blockType}>{t(`emailLayoutBlock_${block.type}`)}</span>
                        <span className={layoutStyles.blockSummary}>{blockSummary(block, t)}</span>
                      </button>
                      {canManage ? (
                        <span className={layoutStyles.blockTools}>
                          <button type="button" onClick={() => moveBlock(index, -1)} disabled={disabled || !canMove(draft.blocks, index, -1)} aria-label={t('emailLayoutMoveUp')}>↑</button>
                          <button type="button" onClick={() => moveBlock(index, 1)} disabled={disabled || !canMove(draft.blocks, index, 1)} aria-label={t('emailLayoutMoveDown')}>↓</button>
                          <button type="button" onClick={() => removeBlock(index)} disabled={disabled || block.type === 'content'} aria-label={t('emailLayoutRemoveBlock')}>✕</button>
                        </span>
                      ) : null}
                    </div>
                    {isOpen && !fixed ? <div className={layoutStyles.blockBody}>{renderBlockFields(block, index)}</div> : null}
                    {isOpen && fixed ? <div className={layoutStyles.blockBody}><p className={styles.muted}>{t(`emailLayoutBlockHelp_${block.type}`)}</p></div> : null}
                  </li>
                );
              })}
            </ol>

            {canManage && availableAddTypes.length > 0 ? (
              <div className={layoutStyles.create}>
                <select className="text-input" value={effectiveAddType} onChange={event => setAddType(event.target.value as EmailLayoutBlockType)} disabled={disabled} aria-label={t('emailLayoutBlockType')}>
                  {availableAddTypes.map(type => <option key={type} value={type}>{t(`emailLayoutBlock_${type}`)}</option>)}
                </select>
                <button type="button" className="btn btn-secondary" onClick={addBlock} disabled={disabled}>
                  {t('emailLayoutAddBlock')}
                </button>
              </div>
            ) : null}

            {canManage ? (
              <>
                <div className={brandStyles.actions}>
                  <span className={`${styles.footerNote} ${isDirty ? styles.footerDirty : ''}`}>
                    {isDirty ? t('emailUnsavedChanges') : t('emailAllSaved')}
                  </span>
                  {isDirty ? (
                    <button type="button" className="btn btn-secondary" onClick={() => baseline && setDraft(baseline)} disabled={busy}>
                      {t('emailDiscardChanges')}
                    </button>
                  ) : null}
                  <button type="button" className="btn btn-primary" onClick={() => { void save(); }} disabled={busy || !isDirty}>
                    {busy ? t('savingEllipsis', { ns: 'admin' }) : t('emailLayoutSave')}
                  </button>
                </div>

                <div className={layoutStyles.testRow}>
                  <Field label={t('emailLayoutTestTo')} hint={isDirty ? t('emailLayoutTestSaveFirst') : t('emailLayoutTestHint')}>
                    <input className="text-input" type="email" value={testTo} onChange={event => setTestTo(event.target.value)} placeholder="you@example.com" disabled={busy} />
                  </Field>
                  <button type="button" className="btn btn-secondary" onClick={() => { void sendTest(); }} disabled={busy || isDirty || !testTo.trim()}>
                    {t('emailLayoutSendTest')}
                  </button>
                </div>
                {testResult ? (
                  <p className={`${styles.testResult} ${testResult.ok ? styles.testOk : styles.testBad}`}>{testResult.message}</p>
                ) : null}
              </>
            ) : null}
          </div>

          <div className={brandStyles.previewPane}>
            <div className={brandStyles.previewHead}>
              <span className={brandStyles.previewTitle}>{t('emailBrandPreview')}</span>
              <ChipGroup<'desktop' | 'mobile'>
                value={previewWidth}
                onChange={setPreviewWidth}
                ariaLabel={t('emailBrandPreview')}
                options={[
                  { id: 'desktop', label: t('emailBrandPreviewDesktop'), icon: 'desktop' },
                  { id: 'mobile', label: t('emailBrandPreviewMobile'), icon: 'mobile' },
                ]}
              />
            </div>
            <div className={brandStyles.previewFrameWrap}>
              {preview ? (
                <iframe
                  title={t('emailBrandPreview')}
                  className={brandStyles.previewFrame}
                  style={{ width: previewWidth === 'mobile' ? 375 : '100%' }}
                  sandbox=""
                  srcDoc={preview.html}
                  data-testid="email-layout-preview"
                />
              ) : <Spinner />}
            </div>
            {preview?.exceedsClipLimit ? (
              <p className={`${styles.small} ${brandStyles.warnText}`}>{t('emailBrandPreviewTooLarge', { size: Math.round(preview.htmlBytes / 1024) })}</p>
            ) : null}
          </div>
        </div>
      ) : busy ? <Spinner /> : null}
    </div>
  );
};
